/**
 * launchSuiteApp — initiate a suite-app launch via the canonical Central
 * launch constructor (H-2A).
 *
 * Calls `POST /api/suite/launch-intent` and navigates the browser to the
 * `launchUrl` it returns. The launch URL is treated as fully OPAQUE — this
 * helper never inspects, parses, or constructs it, and never infers auth
 * behavior from it.
 *
 * Used only for apps that `/api/workspace/current` reported with
 * `oidcLaunch: true`. Bare-link launches do not go through here.
 *
 * If launch-intent does not return a launchable verdict (or the request
 * fails), the browser falls back to `fallbackUrl` — the app's bare URL —
 * so a launch is never silently dropped.
 */
export async function launchSuiteApp(
  appId: string,
  opts: { surface: string; returnTo?: string; fallbackUrl?: string },
): Promise<void> {
  try {
    const res = await fetch('/api/suite/launch-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        appId,
        surface: opts.surface,
        ...(opts.returnTo ? { returnTo: opts.returnTo } : {}),
      }),
    });
    const data = await res.json().catch(() => null);
    if (res.ok && data?.launchable === true && typeof data.launchUrl === 'string') {
      window.location.assign(data.launchUrl);
      return;
    }
  } catch {
    // Fall through to the fallback below.
  }
  if (opts.fallbackUrl) {
    window.location.assign(opts.fallbackUrl);
  }
}
